/* ===========================================================
 * ColumnResizer
 * -----------------------------------------------------------
 * Vertikale Drag-Linie zwischen zwei Composer-Spalten.
 * Sendet beim Ziehen die neue Pixel-Breite an den Caller —
 * Caller persistiert via useDockLayout.
 * =========================================================== */

import { type PointerEvent as RPointerEvent, useCallback, useEffect, useRef, useState } from "react";

type Props = {
  side: "left" | "right";
  /** Aktuelle Breite (für relative Berechnung beim Drag-Start). */
  current: number;
  onChange: (px: number) => void;
  title?: string;
};

export function ColumnResizer({ side, current, onChange, title }: Props): JSX.Element {
  const startRef = useRef<{ x: number; w: number } | null>(null);
  const [active, setActive] = useState(false);

  const onMove = useCallback(
    (ev: PointerEvent): void => {
      const s = startRef.current;
      if (!s) return;
      const dx = ev.clientX - s.x;
      const next = side === "left" ? s.w + dx : s.w - dx;
      onChange(next);
    },
    [onChange, side],
  );

  const onUp = useCallback((): void => {
    startRef.current = null;
    setActive(false);
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, [onMove]);

  useEffect(() => () => onUp(), [onUp]);

  const onDown = (ev: RPointerEvent<HTMLDivElement>): void => {
    ev.preventDefault();
    startRef.current = { x: ev.clientX, w: current };
    setActive(true);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <div
      className={`col-resizer ${active ? "is-active" : ""}`}
      data-side={side}
      onPointerDown={onDown}
      title={title ?? "Ziehen um Breite zu ändern · Doppelklick für Standard"}
      onDoubleClick={() => onChange(side === "left" ? 320 : 340)}
      role="separator"
      aria-orientation="vertical"
    >
      <span className="col-resizer-grip" aria-hidden />
    </div>
  );
}
